/**
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState, createContext, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import { Helmet } from "react-helmet";
import CircularProgress from "@mui/material/CircularProgress";

import Header from "components/Header";
import Footer from "components/Footer";
import ModalTerms from "components/ModalTerms";
import UpdateButton from "components/UpdateButton";
import theme from "assets/theme";
import routes from "routes";

export const TermsContext = createContext();

// Componente para manejar metadatos dinámicos
const DynamicMetaTags = ({ pathname }) => {
  const getMetaTags = () => {
    switch (pathname) {
      case "/":
        return {
          title: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          description: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados. ¡Tu oportunidad de ganar está aquí!",
          keywords: "rifas Venezuela, sorteos Venezuela, premios Venezuela, GANA CON BENDICION EN MANOS, lotería Venezuela, juegos de azar, premios en efectivo, rifas online, sorteos online",
          ogTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          ogDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados.",
          twitterTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          twitterDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados."
        };
        case "/home":
          return {
            title: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
            description: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados. ¡Tu oportunidad de ganar está aquí!",
            keywords: "rifas Venezuela, sorteos Venezuela, premios Venezuela, GANA CON BENDICION EN MANOS, lotería Venezuela, juegos de azar, premios en efectivo, rifas online, sorteos online",
            ogTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
            ogDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados.",
            twitterTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
            twitterDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados."
          };
      case "/verificar":
        return {
          title: "Verificar Ticket - GANA CON BENDICION EN MANOS",
          description: "Verifica el estado de tu ticket de rifa. Ingresa tu número de ticket y confirma si eres ganador de nuestros sorteos.",
          keywords: "verificar ticket, verificar rifa, consultar ticket, verificar número, ganador rifa, verificar premio, GANA CON BENDICION EN MANOS",
          ogTitle: "Verificar Ticket - GANA CON BENDICION EN MANOS",
          ogDescription: "Verifica el estado de tu ticket de rifa. Confirma si eres ganador de nuestros sorteos.",
          twitterTitle: "Verificar Ticket - GANA CON BENDICION EN MANOS",
          twitterDescription: "Verifica el estado de tu ticket de rifa. Confirma si eres ganador de nuestros sorteos."
        };
      case "/terms":
        return {
          title: "Términos y Condiciones - GANA CON BENDICION EN MANOS",
          description: "Conoce los términos y condiciones de participación en nuestras rifas. Información importante sobre el proceso de compra, verificación y entrega de premios.",
          keywords: "términos y condiciones, reglas rifas, condiciones participación, política rifas, GANA CON BENDICION EN MANOS, términos legales",
          ogTitle: "Términos y Condiciones - GANA CON BENDICION EN MANOS",
          ogDescription: "Conoce los términos y condiciones de participación en nuestras rifas.",
          twitterTitle: "Términos y Condiciones - GANA CON BENDICION EN MANOS",
          twitterDescription: "Conoce los términos y condiciones de participación en nuestras rifas."
        };
      default:
        return {
          title: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          description: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados.",
          keywords: "rifas Venezuela, sorteos Venezuela, premios Venezuela, GANA CON BENDICION EN MANOS",
          ogTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          ogDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados.",
          twitterTitle: "GANA CON BENDICION EN MANOS - Sorteos y Premios en Venezuela",
          twitterDescription: "Participa en las mejores rifas de Venezuela. Compra tus números y gana grandes premios garantizados."
        };
    }
  };

  const metaTags = getMetaTags();

  return (
    <Helmet>
      <title>{metaTags.title}</title>
      <meta name="description" content={metaTags.description} />
      <meta name="keywords" content={metaTags.keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={metaTags.ogTitle} />
      <meta property="og:description" content={metaTags.ogDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://ganaconbendicionenmanos.com${pathname}`} />
      
      {/* Twitter */}
      <meta property="twitter:title" content={metaTags.twitterTitle} />
      <meta property="twitter:description" content={metaTags.twitterDescription} />
      <meta property="twitter:card" content="summary_large_image" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={`https://ganaconbendicionenmanos.com${pathname}`} />
    </Helmet>
  );
};

// Componente para manejar rutas no encontradas
const NotFound = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5"
      }}
    >
      <CircularProgress />
    </Box>
  );
};

export default function App() {
  const { pathname } = useLocation();
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsLink, setTermsLink] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [needsUpdate, setNeedsUpdate] = useState(false);

  useEffect(() => {
    // Simular tiempo de carga inicial
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    // Verificar si se necesita actualización
    if (window.needsUpdate) {
      setNeedsUpdate(window.needsUpdate());
    }

    return () => clearTimeout(timer);
  }, []);

  const openTerms = (link) => {
    setTermsLink(link || "");
    setTermsOpen(true);
  };
  const closeTerms = () => setTermsOpen(false);
  const acceptTerms = () => {
    if (termsLink) window.open(termsLink, "_blank");
    setTermsOpen(false);
  };

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) => {
    const routesArray = [];
    allRoutes.forEach((route) => {
      if (route.collapse) {
        routesArray.push(...getRoutes(route.collapse));
      } else if (route.route) {
        routesArray.push(
          <Route exact path={route.route} element={route.component} key={route.name} />
        );
      }
    });
    return routesArray;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TermsContext.Provider value={{ openTerms }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DynamicMetaTags pathname={pathname} />
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <Header />
          <Box component="main" sx={{ flexGrow: 1 }}>
            <Suspense fallback={
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            }>
              <Routes>
                {getRoutes(routes)}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Box>
          <Footer />
          <ModalTerms open={termsOpen} onClose={closeTerms} onAccept={acceptTerms} />
          {needsUpdate && <UpdateButton />}
        </Box>
      </ThemeProvider>
    </TermsContext.Provider>
  );
}
